import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, FlipHorizontal2, RefreshCw, ShieldCheck, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { compareFacePhotos, loadFaceVerificationModels } from "../lib/faceVerification";
import api from "../lib/api";
import { deleteOfflineScannerRecord, getOfflineScannerRecords, replaceOfflineScannerRecord, saveOfflineScannerRecord } from "../lib/offlineScannerQueue";
import { formatDateTime, getErrorMessage } from "../lib/formatters";


const navItems = [{ path: "/scanner/scan", label: "Scan Attendance", shortLabel: "SCAN", testId: "scanner-scan-nav" }];
const DEFAULT_THRESHOLDS = {
  local_auto_approve_threshold: 0.78,
  local_gemini_fallback_threshold: 0.52,
  gemini_approve_threshold: 0.82,
};
const verificationMethodLabels = {
  local: "Local TensorFlow",
  gemini_fallback: "Gemini fallback",
  manual_override: "Manual override",
  manual_review: "Manual review",
};
const verificationStatusLabels = {
  approved: "Approved",
  fallback_candidate: "Fallback queued",
  below_local_threshold: "Retry needed",
  below_gemini_threshold: "Gemini review failed",
  missing_profile_picture: "Missing profile picture",
  face_not_detected: "Face not detected",
  local_verification_unavailable: "Verifier unavailable",
  soft_limit_reached: "Gemini soft limit reached",
  quota_exhausted: "Gemini quota exhausted",
  unavailable: "Gemini unavailable",
  fallback_unavailable: "Fallback unavailable",
  disabled: "Fallback disabled",
};
const formatPercent = (value) => `${Math.round((Number(value) || 0) * 100)}%`;


export default function ScannerAdminPage() {
  const { user, tenant, scannerLocation, logout } = useAuth();
  const [serverDirectory, setServerDirectory] = useState([]);
  const [activity, setActivity] = useState([]);
  const [stationConfig, setStationConfig] = useState({ thresholds: DEFAULT_THRESHOLDS, gemini_fallback: { state: "available", used_today: 0, soft_limit: 0, remaining_today: 0 } });
  const [query, setQuery] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [result, setResult] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraMode, setCameraMode] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches ? "environment" : "user");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [faceVerifierStatus, setFaceVerifierStatus] = useState("loading");
  const [faceVerifierMessage, setFaceVerifierMessage] = useState("Loading local TensorFlow verifier...");
  const [submitting, setSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [offlineRecords, setOfflineRecords] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncSummary, setLastSyncSummary] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const resetTimerRef = useRef(null);
  const scopeKey = useMemo(() => `${tenant?.id || ""}:${user?.id || ""}:${scannerLocation?.id || ""}`, [scannerLocation?.id, tenant?.id, user?.id]);

  const refreshOfflineRecords = async () => {
    if (!scopeKey || !tenant?.id || !user?.id || !scannerLocation?.id) return;
    const records = await getOfflineScannerRecords(scopeKey);
    setOfflineRecords(records);
  };

  const stationThresholds = useMemo(() => stationConfig?.thresholds || DEFAULT_THRESHOLDS, [stationConfig]);

  const loadData = async (searchTerm = "") => {
    try {
      const [employeesResponse, activityResponse, stationResponse] = await Promise.all([
        api.get("/scanner/employees", { params: searchTerm ? { query: searchTerm } : {} }),
        api.get("/scanner/activity"),
        api.get("/scanner/station"),
      ]);
      setServerDirectory(employeesResponse.data.employees);
      setActivity(activityResponse.data);
      setStationConfig(stationResponse.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const directory = useMemo(() => {
    const pendingByEmployee = offlineRecords.filter((item) => item.sync_status === "pending").reduce((acc, item) => {
      acc[item.employee_id] = item;
      return acc;
    }, {});
    return serverDirectory.map((employee) => {
      const pendingRecord = pendingByEmployee[employee.id];
      if (!pendingRecord) return employee;
      return {
        ...employee,
        current_status: pendingRecord.action === "clock_in" ? "Pending Clock In" : "Pending Clock Out",
        next_action: pendingRecord.action === "clock_in" ? "clock_out" : "clock_in",
      };
    });
  }, [offlineRecords, serverDirectory]);

  const selectedEmployee = useMemo(() => directory.find((item) => item.id === selectedEmployeeId) || null, [directory, selectedEmployeeId]);
  const pendingRecords = useMemo(() => offlineRecords.filter((item) => item.sync_status === "pending"), [offlineRecords]);
  const failedRecords = useMemo(() => offlineRecords.filter((item) => item.sync_status === "failed" || item.sync_status === "conflict"), [offlineRecords]);
  const pendingVerificationCount = useMemo(() => pendingRecords.filter((item) => item.verification_status !== "approved").length, [pendingRecords]);

  useEffect(() => {
    loadData(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    refreshOfflineRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const startCamera = async (mode = cameraMode) => {
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: mode } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraReady(true);
    } catch {
      try {
        const fallback = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = fallback;
        if (videoRef.current) videoRef.current.srcObject = fallback;
        setCameraReady(true);
      } catch {
        setCameraReady(false);
        toast.error("Camera access is required for photo scanner attendance");
      }
    }
  };

  const scheduleReset = (delay = 2000) => {
    window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(() => {
      setResult(null);
      setSelectedEmployeeId("");
    }, delay);
  };

  useEffect(() => {
    const boot = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setHasMultipleCameras(devices.filter((device) => device.kind === "videoinput").length > 1);
      } catch {
        setHasMultipleCameras(false);
      }
      try {
        await loadFaceVerificationModels();
        setFaceVerifierStatus("ready");
        setFaceVerifierMessage("Local TensorFlow face verification is ready.");
      } catch (error) {
        setFaceVerifierStatus("error");
        setFaceVerifierMessage(error.message || "Local face verification could not be loaded.");
      }
      await startCamera(cameraMode);
    };
    boot();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      stopCamera();
      window.clearTimeout(resetTimerRef.current);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.92);
  };

  const buildVerificationPayload = async (photo, employee, manualOverride = false) => {
    const localResult = await compareFacePhotos({
      livePhotoData: photo,
      profilePhotoData: employee?.profile_picture_data,
    });
    if (localResult.status === "local_verification_unavailable" && faceVerifierStatus !== "ready") {
      setFaceVerifierStatus("error");
      setFaceVerifierMessage(localResult.summary);
    }
    let verificationMethod = "manual_review";
    let verificationStatus = localResult.status;
    if (manualOverride) {
      verificationMethod = "manual_override";
      verificationStatus = "approved";
    } else if (localResult.confidence !== null && localResult.match && localResult.confidence >= stationThresholds.local_auto_approve_threshold) {
      verificationMethod = "local";
      verificationStatus = "approved";
    } else if (localResult.confidence !== null && localResult.confidence >= stationThresholds.local_gemini_fallback_threshold) {
      verificationMethod = "gemini_fallback";
      verificationStatus = "fallback_candidate";
    }
    return {
      verification_method: verificationMethod,
      verification_status: verificationStatus,
      local_confidence_score: localResult.confidence,
      local_face_distance: localResult.distance,
      local_match: localResult.match,
      local_summary: localResult.summary,
      local_concerns: localResult.concerns || [],
      threshold_snapshot: stationThresholds,
    };
  };

  const queueOfflineRecord = async () => {
    if (!selectedEmployee || !scannerLocation) return;
    const photo = captureFrame();
    if (!photo) {
      toast.error("Camera frame could not be captured");
      return;
    }
    const verificationPayload = await buildVerificationPayload(photo, selectedEmployee, false);
    const record = {
      id: window.crypto.randomUUID(),
      employee_id: selectedEmployee.id,
      employee_name: selectedEmployee.name,
      tenant_id: tenant.id,
      location_id: scannerLocation.id,
      location_name: scannerLocation.name,
      scanner_admin_id: user.id,
      business_unit_id: selectedEmployee.business_unit_id,
      business_unit_name: selectedEmployee.business_unit_name,
      action: selectedEmployee.next_action || "clock_in",
      timestamp: new Date().toISOString(),
      captured_selfie_data: photo,
      sync_status: "pending",
      verification_method: verificationPayload.verification_method,
      verification_status: verificationPayload.verification_status,
      local_confidence_score: verificationPayload.local_confidence_score,
      local_face_distance: verificationPayload.local_face_distance,
      local_match: verificationPayload.local_match,
      local_summary: verificationPayload.local_summary,
      local_concerns: verificationPayload.local_concerns,
      threshold_snapshot: verificationPayload.threshold_snapshot,
      failure_reason: null,
    };
    await saveOfflineScannerRecord(record, scopeKey);
    await refreshOfflineRecords();
    setResult({
      status: "queued_offline",
      user_message: verificationPayload.verification_method === "gemini_fallback"
        ? "Offline mode active. Local check is uncertain, so Gemini fallback will run during sync."
        : verificationPayload.verification_method === "local"
          ? "Offline mode active. Local verification passed and the attendance will be confirmed during sync."
          : "Offline mode active. This capture will need review when the connection returns.",
      employee: { name: selectedEmployee.name },
      attendance: { action: record.action, timestamp: record.timestamp },
      verification: {
        method: verificationPayload.verification_method,
        status: verificationPayload.verification_status,
        threshold_snapshot: verificationPayload.threshold_snapshot,
      },
      match: {
        confidence: verificationPayload.local_confidence_score,
        summary: verificationPayload.local_summary,
        method: verificationPayload.verification_method,
      },
    });
    scheduleReset(1400);
  };

  const syncOfflineQueue = async () => {
    if (!pendingRecords.length || !isOnline) return;
    setSyncing(true);
    try {
      const response = await api.post("/attendance/photo-scanner/sync", {
        records: pendingRecords.map((item) => ({
          offline_id: item.id,
          employee_id: item.employee_id,
          tenant_id: item.tenant_id,
          location_id: item.location_id,
          scanner_admin_id: item.scanner_admin_id,
          action: item.action,
          timestamp: item.timestamp,
          captured_selfie_data: item.captured_selfie_data,
          verification_method: item.verification_method,
          verification_status: item.verification_status,
          local_confidence_score: item.local_confidence_score,
          local_face_distance: item.local_face_distance,
          local_match: item.local_match,
          local_summary: item.local_summary,
          local_concerns: item.local_concerns || [],
          threshold_snapshot: item.threshold_snapshot,
        })),
      });
      for (const item of response.data.results) {
        const existing = pendingRecords.find((record) => record.id === item.offline_id);
        if (!existing) continue;
        if (item.status === "confirmed") {
          await deleteOfflineScannerRecord(item.offline_id);
        } else {
          await replaceOfflineScannerRecord({
            ...existing,
            sync_status: item.status,
            verification_status: item.verification_status || (item.status === "conflict" ? "conflict" : "failed"),
            verification_method: item.verification_method || existing.verification_method,
            failure_reason: item.reason,
          }, scopeKey);
        }
      }
      setLastSyncSummary({
        confirmed: response.data.confirmed_count,
        failed: response.data.failed_count,
        conflicts: response.data.conflict_count,
      });
      await refreshOfflineRecords();
      await loadData(query);
      toast.success(`Sync complete: ${response.data.confirmed_count} confirmed, ${response.data.failed_count} failed`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (isOnline && pendingRecords.length) {
      syncOfflineQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const submitScan = async (manualOverride = false) => {
    if (!selectedEmployee) {
      toast.error("Select an employee first");
      return;
    }
    if (!isOnline) {
      await queueOfflineRecord();
      return;
    }
    const photo = captureFrame();
    if (!photo) {
      toast.error("Camera frame could not be captured");
      return;
    }
    setSubmitting(true);
    window.clearTimeout(resetTimerRef.current);
    try {
      const verificationPayload = await buildVerificationPayload(photo, selectedEmployee, manualOverride);
      const response = await api.post("/attendance/photo-scanner/verify", {
        employee_id: selectedEmployee.id,
        live_photo_data: photo,
        manual_override: manualOverride,
        override_reason: manualOverride ? "Scanner Admin override after low-confidence review" : undefined,
        ...verificationPayload,
      });
      setResult(response.data);
      if (response.data.status === "success") {
        await loadData(query);
        scheduleReset(1800);
      }
    } catch (error) {
      setResult({ status: "error", message: getErrorMessage(error) });
      scheduleReset(2400);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    window.clearTimeout(resetTimerRef.current);
    setResult(null);
  };

  const handleFlipCamera = async () => {
    const next = cameraMode === "environment" ? "user" : "environment";
    setCameraMode(next);
    await startCamera(next);
  };

  return (
    <AppShell
      title="Scan Attendance"
      subtitle="Local TensorFlow verification runs first. Gemini is used only for uncertain matches, while offline captures stay queued until sync."
      navItems={navItems}
      user={user}
      tenant={tenant}
      onLogout={logout}
      actions={<div className="rounded-full border border-[#F4A300]/20 bg-[#F4A300]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#C27D00]" data-testid="scanner-location-pill">{scannerLocation?.name || "Location pending"}</div>}
    >
      {!isOnline ? <div className="mb-6 rounded-[28px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-800" data-testid="scanner-offline-banner">Offline mode active. Attendance will be verified when connection returns.</div> : null}

      <div className="grid gap-6 xl:grid-cols-[1.45fr_0.85fr]" data-testid="scanner-admin-layout">
        <Card className="border-[#0D1B2A]/10 bg-white shadow-[0_18px_50px_rgba(13,27,42,0.08)]" data-testid="scanner-camera-card">
          <CardHeader>
            <CardTitle data-testid="scanner-camera-title">Live camera station</CardTitle>
            <CardDescription data-testid="scanner-camera-description">Stay on this screen and continue processing employees. Online mode runs local TensorFlow verification first, then uses Gemini only for uncertain matches.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="space-y-3">
                <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name, email, or employee code" data-testid="scanner-search-input" />
                <div className="max-h-[420px] overflow-y-auto rounded-3xl border border-slate-200 bg-slate-50/70 p-3" data-testid="scanner-employee-list">
                  {directory.map((employee) => (
                    <button
                      key={employee.id}
                      type="button"
                      className={`mb-2 w-full rounded-2xl border px-4 py-3 text-left transition ${selectedEmployeeId === employee.id ? "border-[#F4A300]/30 bg-[#F4A300]/10" : "border-slate-200 bg-white hover:border-[#0D1B2A]/15"}`}
                      onClick={() => setSelectedEmployeeId(employee.id)}
                      data-testid={`scanner-employee-option-${employee.id}`}
                    >
                      <p className="font-semibold text-slate-900">{employee.name}</p>
                      <p className="text-xs text-slate-500">{employee.employee_code} • {employee.location_name}</p>
                      <p className="text-xs text-slate-500">{employee.business_unit_name || "Business unit pending"}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#C27D00]">Next action: {employee.next_action?.replaceAll("_", " ")}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="relative overflow-hidden rounded-[28px] border border-[#0D1B2A]/10 bg-[#0D1B2A]" data-testid="scanner-camera-preview-wrap">
                  <video ref={videoRef} autoPlay playsInline muted className="h-[360px] w-full object-cover lg:h-[520px]" data-testid="scanner-camera-preview" />
                  {!cameraReady ? <div className="absolute inset-0 flex items-center justify-center bg-[#0D1B2A]/80 text-sm font-semibold text-white" data-testid="scanner-camera-loading">Waiting for camera access…</div> : null}
                  <div className="absolute left-4 top-4 rounded-full bg-black/45 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white" data-testid="scanner-camera-mode-pill">{cameraMode === "environment" ? "Back camera" : "Front camera"}</div>
                </div>
                <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4" data-testid="scanner-selected-employee-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#C27D00]">Current selection</p>
                  <p className="mt-2 text-xl font-bold text-slate-900" data-testid="scanner-selected-employee-name">{selectedEmployee?.name || "Select an employee"}</p>
                  <p className="mt-1 text-sm text-slate-500" data-testid="scanner-selected-employee-meta">{selectedEmployee ? `${selectedEmployee.location_name} • ${selectedEmployee.business_unit_name || "—"} • ${selectedEmployee.next_action?.replaceAll("_", " ")}` : "Select the next employee from your location-specific queue."}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" data-testid="scanner-selected-employee-profile-status">{selectedEmployee ? (selectedEmployee.has_profile_picture ? "Profile picture ready" : "Profile picture missing") : "Verification asset pending"}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => submitScan(false)} disabled={submitting || !cameraReady || !selectedEmployee || !selectedEmployee.next_action} data-testid="scanner-capture-button">
                    <Camera className="mr-2 h-4 w-4" />
                    {submitting ? "Processing..." : isOnline ? "Capture & verify" : "Capture offline"}
                  </Button>
                  <Button type="button" variant="outline" className="rounded-2xl" onClick={handleFlipCamera} disabled={!hasMultipleCameras} data-testid="scanner-flip-camera-button">
                    <FlipHorizontal2 className="mr-2 h-4 w-4" />
                    Flip Camera
                  </Button>
                  {result?.status === "review_required" ? (
                    <>
                      <Button type="button" variant="outline" className="rounded-2xl" onClick={handleRetry} disabled={submitting} data-testid="scanner-retry-button">Retry capture</Button>
                      <Button type="button" variant="outline" className="rounded-2xl" onClick={() => submitScan(true)} disabled={submitting} data-testid="scanner-override-button">
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Override & mark attendance
                      </Button>
                    </>
                  ) : null}
                  <Button type="button" variant="outline" className="rounded-2xl" onClick={() => loadData(query)} data-testid="scanner-refresh-button">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh queue
                  </Button>
                </div>
                {result ? (
                  <div className={`rounded-3xl border p-4 ${result.status === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : result.status === "queued_offline" ? "border-sky-200 bg-sky-50 text-sky-800" : result.status === "review_required" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-rose-200 bg-rose-50 text-rose-700"}`} data-testid="scanner-result-card">
                    <p className="text-sm font-semibold" data-testid="scanner-result-status">{result.status === "success" ? "Attendance marked" : result.status === "queued_offline" ? "Saved offline" : result.status === "review_required" ? "Review required" : "Scan error"}</p>
                    {result.employee ? <p className="mt-2 text-lg font-bold" data-testid="scanner-result-employee-name">{result.employee.name}</p> : null}
                    <p className="mt-2 text-sm font-semibold" data-testid="scanner-result-summary">{result.user_message || result.match?.summary || result.summary || result.message}</p>
                    {result.verification ? <p className="mt-2 text-xs uppercase tracking-[0.2em]" data-testid="scanner-result-verification-method">{verificationMethodLabels[result.verification.method] || result.verification.method} • {verificationStatusLabels[result.verification.status] || result.verification.status || result.verification.status}</p> : null}
                    {result.status === "review_required" ? <p className="mt-2 text-xs text-amber-800/90" data-testid="scanner-result-guidance">{result.verification?.status === "soft_limit_reached" || result.verification?.status === "quota_exhausted" || result.verification?.status === "fallback_unavailable" ? "Gemini fallback is unavailable for this uncertain match. Retry the capture or use manual override." : "Retry the capture or use manual override if the employee is physically present."}</p> : null}
                    {result.attendance ? <p className="mt-2 text-sm font-semibold" data-testid="scanner-result-action">{result.attendance.action?.replaceAll("_", " ")} • {formatDateTime(result.attendance.timestamp)}</p> : null}
                    {result.match?.confidence !== undefined ? <p className="mt-1 text-xs uppercase tracking-[0.2em]" data-testid="scanner-result-confidence">Confidence {(result.match?.confidence * 100).toFixed(0)}%</p> : null}
                    {result.verification?.quota ? <p className="mt-1 text-xs uppercase tracking-[0.2em]" data-testid="scanner-result-quota-status">Gemini {result.verification.quota.used_today}/{result.verification.quota.soft_limit} today • {verificationStatusLabels[result.verification.quota.state] || result.verification.quota.state}</p> : null}
                    <p className="mt-2 text-xs uppercase tracking-[0.2em]" data-testid="scanner-result-reset-note">Success and error results auto-reset. Review states stay active for action.</p>
                  </div>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-slate-200/70 bg-white/90" data-testid="scanner-station-card">
            <CardHeader>
              <CardTitle data-testid="scanner-station-title">Scanner station</CardTitle>
              <CardDescription data-testid="scanner-station-description">Your queue is limited to your assigned location and all offline records are revalidated during sync.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600">
              <div className="flex items-center justify-between" data-testid="scanner-online-status-row">
                <span className="font-semibold text-slate-900">Connection</span>
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${isOnline ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="scanner-online-status-pill">{isOnline ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}{isOnline ? "Online" : "Offline"}</span>
              </div>
              <p data-testid="scanner-station-location"><span className="font-semibold text-slate-900">Location:</span> {scannerLocation?.name || "Not configured"}</p>
              <p data-testid="scanner-local-verifier-status"><span className="font-semibold text-slate-900">Local verifier:</span> {faceVerifierStatus === "ready" ? "Ready" : faceVerifierStatus === "error" ? "Needs attention" : "Loading"}</p>
              <p className="text-xs text-slate-500" data-testid="scanner-local-verifier-message">{faceVerifierMessage}</p>
              <p data-testid="scanner-gemini-fallback-usage"><span className="font-semibold text-slate-900">Gemini fallback:</span> {stationConfig?.gemini_fallback?.soft_limit ? `${stationConfig.gemini_fallback.used_today}/${stationConfig.gemini_fallback.soft_limit} today` : "Disabled"} • {verificationStatusLabels[stationConfig?.gemini_fallback?.state] || stationConfig?.gemini_fallback?.state}</p>
              <p data-testid="scanner-threshold-summary"><span className="font-semibold text-slate-900">Thresholds:</span> Local auto {formatPercent(stationThresholds.local_auto_approve_threshold)} • Gemini fallback from {formatPercent(stationThresholds.local_gemini_fallback_threshold)}</p>
              <p data-testid="scanner-pending-sync-count"><span className="font-semibold text-slate-900">Pending sync:</span> {pendingRecords.length}</p>
              <p data-testid="scanner-pending-verification-count"><span className="font-semibold text-slate-900">Pending verification:</span> {pendingVerificationCount}</p>
              <p data-testid="scanner-failed-sync-count"><span className="font-semibold text-slate-900">Failed sync items:</span> {failedRecords.length}</p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" disabled={!isOnline || syncing || !pendingRecords.length} onClick={syncOfflineQueue} data-testid="scanner-sync-now-button">{syncing ? "Syncing..." : "Sync Now"}</Button>
              </div>
              {lastSyncSummary ? <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="scanner-last-sync-summary">Confirmed {lastSyncSummary.confirmed} • Failed {lastSyncSummary.failed} • Conflicts {lastSyncSummary.conflicts}</div> : null}
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 bg-white/90" data-testid="scanner-activity-card">
            <CardHeader>
              <CardTitle data-testid="scanner-activity-title">Recent scan activity</CardTitle>
              <CardDescription data-testid="scanner-activity-description">Server-verified activity plus failed offline sync items are shown separately below.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {activity.map((item) => (
                <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4" data-testid={`scanner-activity-item-${item.id}`}>
                  <p className="font-semibold text-slate-900">{item.employee_name || "Unknown employee"}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{item.outcome} • {item.action || "review"}</p>
                  {item.verification_method ? <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#C27D00]" data-testid={`scanner-activity-verification-${item.id}`}>{verificationMethodLabels[item.verification_method] || item.verification_method} • {verificationStatusLabels[item.verification_status] || item.verification_status}</p> : null}
                  <p className="mt-2 text-sm text-slate-500">{item.location_name} • {item.business_unit_name || "—"}</p>
                  <p className="mt-2 text-xs text-slate-500">{formatDateTime(item.created_at)}</p>
                </div>
              ))}
              {!activity.length ? <p className="text-sm text-slate-400" data-testid="scanner-activity-empty">No scan activity yet.</p> : null}
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 bg-white/90" data-testid="scanner-failed-queue-card">
            <CardHeader>
              <CardTitle data-testid="scanner-failed-queue-title">Failed offline sync items</CardTitle>
              <CardDescription data-testid="scanner-failed-queue-description">Failed or conflicting items stay visible until manually cleared or retried.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {failedRecords.map((item) => (
                <div key={item.id} className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800" data-testid={`scanner-failed-item-${item.id}`}>
                  <p className="font-semibold">{item.employee_name}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em]">{item.sync_status} • {item.action?.replaceAll("_", " ")}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em]" data-testid={`scanner-failed-verification-${item.id}`}>{verificationMethodLabels[item.verification_method] || item.verification_method || "Verification pending"} • {verificationStatusLabels[item.verification_status] || item.verification_status || "pending"}</p>
                  <p className="mt-2 text-sm">{item.failure_reason || "Attendance could not be confirmed during sync."}</p>
                  <div className="mt-3 flex gap-2">
                    <Button type="button" variant="outline" className="rounded-2xl" onClick={() => deleteOfflineScannerRecord(item.id).then(refreshOfflineRecords)} data-testid={`scanner-failed-clear-${item.id}`}>Clear</Button>
                  </div>
                </div>
              ))}
              {!failedRecords.length ? <p className="text-sm text-slate-400" data-testid="scanner-failed-queue-empty">No failed offline records.</p> : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}